import config from "@/config";
import router from "@/router";
import { useSnackBarStore } from "@/stores/snackBar";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const axiosIns = axios.create({
  baseURL: `${config.BASE_URL}api/v1/`,
  // timeout: 2000,
  headers: {
    "x-api-key": `x5b9j8p2qRz3vdK1st7yf4ul6wa0ezcv`,
    "Accept-Language": "en-US",
  },
});

// Queue for failed requests during token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

axiosIns.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Retrieve token from localStorage
  const token = localStorage.getItem("accessToken");

  // If token is found
  if (token) {
    // Get request headers and if headers is undefined assign blank object
    config.headers = config.headers || {};

    // Set authorization header
    // ℹ️ JSON.parse will convert token to string
    config.headers.Authorization = token ? `Bearer ${JSON.parse(token)}` : "";
  }

  // Return modified config
  return config;
});

// Helper function to map camelCase to underscore format for product fields
const mapProductFields = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => mapProductFields(item));
  }
  
  const mapped: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // Map specific camelCase fields to underscore format
      const fieldMap: Record<string, string> = {
        'displayNameEn': 'displayName_En',
        'displayNameAr': 'displayName_Ar',
        'descriptionEn': 'description_En',
        'descriptionAr': 'description_Ar',
        'generalInfoEn': 'generalInfo_En',
        'generalInfoAr': 'generalInfo_Ar',
        'suggestedUseEn': 'suggestedUse_En',
        'suggestedUseAr': 'suggestedUse_Ar',
        'categoryDisplayNameEn': 'categoryDisplayName_En',
        'categoryDisplayNameAr': 'categoryDisplayName_Ar',
        'subCategoryDisplayNameEn': 'subCategoryDisplayName_En',
        'subCategoryDisplayNameAr': 'subCategoryDisplayName_Ar',
        'brandDisplayNameEn': 'brandDisplayName_En',
        'brandDisplayNameAr': 'brandDisplayName_Ar',
        'productDisplayNameEn': 'productDisplayName_En',
        'productDisplayNameAr': 'productDisplayName_Ar',
      };
      
      const mappedKey = fieldMap[key] || key;
      mapped[mappedKey] = mapProductFields(obj[key]);
    }
  }
  return mapped;
};

axiosIns.interceptors.response.use(
  (response) => {
    // Map product field names from camelCase to underscore format
    if (response.data) {
      response.data = mapProductFields(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If the response has a 401 status code, try to refresh token
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints
      if (originalRequest.url?.includes('/auth/')) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        router.push({ name: "login" });
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosIns(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        isRefreshing = false;
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        router.push({ name: "login" });
        return Promise.reject(error);
      }

      try {
        // Call refresh token endpoint
        const parsedRefreshToken = JSON.parse(refreshToken);
        const response = await axios.post(
          `${config.BASE_URL}api/v1/auth/refresh`,
          { refreshToken: parsedRefreshToken },
          {
            headers: {
              "x-api-key": "x5b9j8p2qRz3vdK1st7yf4ul6wa0ezcv",
              "Accept-Language": "en-US",
            },
          }
        );

        if (response.data?.data?.accessToken) {
          const newAccessToken = response.data.data.accessToken;
          const newRefreshToken = response.data.data.refreshToken;

          // Store new tokens
          localStorage.setItem("accessToken", JSON.stringify(newAccessToken));
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", JSON.stringify(newRefreshToken));
          }

          // Update authorization header
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Process queued requests
          processQueue();
          isRefreshing = false;

          // Retry original request
          return axiosIns(originalRequest);
        } else {
          throw new Error("Invalid refresh response");
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError);
        isRefreshing = false;
        
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        router.push({ name: "login" });
        
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden
    if (error.response && error.response.status === 403) {
      const snackBarStore = useSnackBarStore();
      snackBarStore.showSnackBar(
        "Access Denied: You do not have permission to perform this action",
        "#EB5757"
      );
    }

    // For other errors, reject the promise and pass the error along
    return Promise.reject(error);
  }
);

export default axiosIns;
